import { useEffect, useRef } from 'react'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import { useThreeDStudioStore, threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { clearOpenedModel, setOpenedModel } from 'Pods/ThreeDStudio/Store/actions/ui'

import { parseRunIdFromPathname } from './parseRunIdFromPathname'
import { parseRunSlugFromPathname } from './parseRunSlugFromPathname'

// Keeps ui.openedModel in sync with the URL. On /ai-3d-model-generator/3d-models/<slug> it fetches
// the run from the public show endpoint and opens it; on /ai or the gallery list
// it clears the opened model. Reacts to URL changes only. The skip check keys on
// the run id (encoded in the slug's `-<id>` suffix) to avoid refetching an
// already-open model.
const useModelUrlSync = (initialRunSlug) => {
  const { getData } = useApiGet()
  const location = useLocation()
  const pathname = location?.pathname ?? ''
  // Set by the variation card click so the variations tab survives the model switch.
  const keepVariationsTab = location?.state?.keepVariationsTab === true
  const previousPathnameRef = useRef(null)
  const latestRequestSlugRef = useRef(null)
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.id ?? null)
  // A run still in flight has nothing to show on the public endpoint yet, so asking for it
  // only yields a 404. Reachable whenever the URL names an in-flight run without the store
  // holding its payload - a generating card opened after a page reload, where the restored
  // run has no openedModel snapshot to satisfy the skip check below. Completion is handled
  // by useGeneratorRunChannel, which hydrates the store from the broadcast and aligns the
  // URL itself, so nothing here needs to re-fetch once the run settles.
  const pathnameRunId = parseRunIdFromPathname(pathname)
  const isTargetRunInFlight = useThreeDStudioStore(state => (
    pathnameRunId != null
    && state.generation.activeRuns.some(run => String(run.runId) === String(pathnameRunId))
  ))

  useEffect(() => {
    const previousPathname = previousPathnameRef.current
    const isFirstRun = previousPathname === null
    previousPathnameRef.current = pathname

    if (!isFirstRun && previousPathname === pathname) {
      return
    }

    const parsedSlug = parseRunSlugFromPathname(pathname)
    const targetSlug = isFirstRun && initialRunSlug != null ? initialRunSlug : parsedSlug

    if (targetSlug == null) {
      latestRequestSlugRef.current = null
      if (threeDStudioStore.getState().ui?.openedModel?.id != null) {
        clearOpenedModel()
      }
      return
    }

    const targetRunId = parseRunIdFromPathname(pathname)
    if (openedModelId != null && targetRunId != null && String(targetRunId) === String(openedModelId)) {
      return
    }

    if (isTargetRunInFlight) {
      latestRequestSlugRef.current = null
      return
    }

    const requestSlug = String(targetSlug)
    latestRequestSlugRef.current = requestSlug

    getData({
      path: `/api/internal/generator/community-models/${targetSlug}`,
      onSuccess: (response) => {
        if (latestRequestSlugRef.current !== requestSlug) {
          return
        }

        const attributes = response?.data?.data?.attributes
        if (!attributes) {
          return
        }
        setOpenedModel(attributes, { keepVariationsTab })
      },
    })
  }, [getData, initialRunSlug, pathname, openedModelId, keepVariationsTab, isTargetRunInFlight])
}

export default useModelUrlSync
